// MARK://dhk8/admin-shell/lib/clients/catalog
// Catálogo del comerciante.
//
// LECTURAS (F1.1 / ADR-0009): productos + categorías + insumos se leen por el
// GRAFO federado (subgraph base erp-products) vía `gqlClient` + queries tipadas
// (AdminProductsQuery / AdminCategoriesQuery). El tenant lo impone el JWT en cada
// subgraph (RLS dhk8_app); NUNCA se manda `tenantId` como argumento (anti-IDOR).
//
// ESCRITURAS (create/update/delete/availability + modificadores): por el GRAFO
// (createProduct/updateProduct/... en erp-products, tenant del JWT, RLS). F6.2 R3a
// migró createModifier (type=PRODUCT tagged 'MODIFICADOR') a createProduct con el
// nuevo `tags` aditivo del input. Con PREVIEW_STUB activo se sirve la copia
// mutable de lib/mock-data, así el CRUD funciona sin backend.
import { PREVIEW_STUB, mock } from '../env.js'
import * as M from '../mock-data.js'
import { gqlClient } from '../gql.js'
import { AdminProductsQuery, AdminProductQuery, AdminComboProductsQuery, AdminCategoriesQuery, AdminTaxProfilesQuery, AdminProductVariantsQuery, AdminPriceListsQuery, AdminCreateProductMutation, AdminUpdateProductMutation, AdminSetProductAvailabilityMutation, AdminDeleteProductMutation, AdminCreateProductCategoryMutation, AdminUpdateProductCategoryMutation, AdminDeleteProductCategoryMutation, AdminAddProductVariantMutation, AdminDeleteProductVariantMutation, AdminCreatePriceListMutation, AdminUpdatePriceListMutation, AdminDeletePriceListMutation, AdminAddPriceListItemMutation, AdminRemovePriceListItemMutation, AdminVariantAxesQuery, AdminVariantSkusQuery, AdminUpsertVariantAxisMutation, AdminDeleteVariantAxisMutation, AdminUpsertVariantOptionMutation, AdminDeleteVariantOptionMutation, AdminUpsertVariantSkuMutation, AdminDeleteVariantSkuMutation } from '../queries/catalog.queries.js'

// ── API DTOs (dhk8-erp-products) ───────────────────────────────────────────
export interface ApiProduct {
  id: string
  tenantId: string
  sku: string
  name: string
  description?: string | null
  type: 'PRODUCT' | 'SERVICE' | 'COMBO' | 'INGREDIENT'
  priceCents: number
  currency: string
  active: boolean
  categoryId?: string | null
  imageUrl?: string | null
  unit?: string | null
  tracksInventory?: boolean | null
  consumesRecipe?: boolean | null
  satProductKey?: string | null
  satUnitKey?: string | null
}
interface ApiCategory { id: string; tenantId: string; name: string; parentId?: string | null; stationId?: string | null; sortOrder?: number | null; active?: boolean | null; publicVisible?: boolean | null }


// ── adapters (API DTO ↔ UI type) ───────────────────────────────────────────
// Carry over `type` + `tags` from the API DTO so consumers (RecipeDetailPage,
// PurchaseOrderDetailPage, InventoryPage) can:
//   1. resolve INGREDIENT references by name (was rendering UUIDs when the
//      backing item was an INGREDIENT but the page only loaded type=PRODUCT)
//   2. surface the `needs-attention:cost-0` tag we set on cost-0 ingredients
//      onboarded with #DIV/0! in the Excel — so the merchant knows which
//      insumos still need a price before they cost recipes.
export function toUiProduct(p: ApiProduct & { type?: ApiProduct['type']; tags?: string[] }): M.Product {
  return {
    id: p.id,
    name: p.name,
    sku: p.sku,
    priceCents: p.priceCents,
    categoryId: p.categoryId ?? '',
    available: p.active,
    taxRate: '0.16', // tax handling lives elsewhere; the master doesn't store it
    ...(p.description != null ? { description: p.description } : {}),
    ...(p.unit != null ? { unit: p.unit } : {}),
    imageUrl: p.imageUrl ?? null,
    ...(p.type ? { type: p.type } : {}),
    ...(p.tags ? { tags: p.tags } : {}),
    tracksInventory: p.tracksInventory ?? false,
    consumesRecipe: p.consumesRecipe ?? false,
    satProductKey: p.satProductKey ?? undefined,
    satUnitKey: p.satUnitKey ?? undefined,
  }
}
function toUiCategory(c: ApiCategory, idx: number): M.Category {
  // sortOrder/active ya vienen del backend (F0.1). Fallback al índice solo si el
  // grafo aún no los devuelve (compat durante el rollout).
  return { id: c.id, name: c.name, sortOrder: c.sortOrder ?? idx + 1, active: c.active ?? true, stationId: c.stationId ?? null, publicVisible: c.publicVisible ?? true }
}

// ── mutable preview store ──────────────────────────────────────────────────
const db = {
  categories: structuredClone(M.CATEGORIES),
  products: structuredClone(M.PRODUCTS) as M.Product[],
  variants: structuredClone(M.VARIANTS),
  modifiers: structuredClone(M.MODIFIERS),
  combos: structuredClone(M.COMBOS),
  priceLists: structuredClone(M.PRICE_LISTS),
}
const id = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`

// Helper de lectura tenant-scoped por el grafo. Pagina la query `products`
// (tope backend 100, ADR-0009) hasta traer el catálogo completo y normaliza
// cada item del grafo al DTO `ApiProduct` que consumen los adapters de UI.
async function fetchAllProductsFromGraph(
  type: string | undefined,
  signal?: AbortSignal,
): Promise<(ApiProduct & { type: ApiProduct['type']; tags: string[] })[]> {
  // network-only: el document-cache de urql no invalida la query tras
  // delete/create/update (deleteProduct devuelve Boolean, sin __typename de Product)
  // → reload() servía datos viejos. Forzamos red para que la vista refleje el cambio.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const all: (ApiProduct & { type: ApiProduct['type']; tags: string[] })[] = []
  for (let page = 1; page < 100; page++) {
    const res = await gqlClient
      .query(AdminProductsQuery, { page, limit: 100, ...(type !== undefined ? { type } : {}) }, ctx)
      .toPromise()
    if (res.error) throw res.error
    const pageData = res.data?.products
    const items = pageData?.items ?? []
    for (const p of items) {
      all.push({
        id: p.id,
        tenantId: p.tenantId,
        sku: p.sku,
        name: p.name,
        description: p.description ?? null,
        type: p.type as ApiProduct['type'],
        priceCents: p.priceCents,
        currency: p.currency,
        active: p.active,
        categoryId: p.categoryId ?? null,
        imageUrl: p.imageUrl ?? null,
        unit: p.unit ?? null,
        tags: p.tags ?? [],
        tracksInventory: p.tracksInventory ?? false,
        consumesRecipe: p.consumesRecipe ?? false,
        satProductKey: p.satProductKey ?? null,
        satUnitKey: p.satUnitKey ?? null,
      })
    }
    const total = pageData?.total ?? items.length
    if (items.length < 100 || all.length >= total) break
  }
  return all
}

// ── categories ─────────────────────────────────────────────────────────────
export async function listCategories(signal?: AbortSignal): Promise<M.Category[]> {
  if (PREVIEW_STUB) return mock(db.categories)
  // network-only: el document-cache de urql no invalida la query tras
  // delete/create/update (deleteProduct devuelve Boolean, sin __typename de Product)
  // → reload() servía datos viejos. Forzamos red para que la vista refleje el cambio.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(AdminCategoriesQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  const rows = res.data?.categories ?? []
  return rows.map((c, i) => toUiCategory(c, i))
}

// NB: las conversiones de unidad (equivalencias) se movieron a lib/clients/units.ts
// (listUnitConversions/upsertUnitConversion/deleteUnitConversion) con la nueva forma
// del grafo (sin input, con `global`). Aquí ya no viven.

// A4 / canon §2.1 + D1: perfiles de impuesto (globales + del tenant). Página de
// gestión en la pasada de UI; aquí solo el cliente de lectura.
export interface UiTaxProfile { id: string; tenantId: string | null; code: string; name: string; impuesto: string; tipoFactor: string; tasaOCuota: number; objetoImp: string; traslado: boolean; retencion: boolean; validFrom: string; validTo: string | null; active: boolean }
export async function listTaxProfiles(signal?: AbortSignal): Promise<UiTaxProfile[]> {
  if (PREVIEW_STUB) return mock([] as UiTaxProfile[])
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(AdminTaxProfilesQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.taxProfiles ?? []).map((t) => ({ id: t.id, tenantId: t.tenantId ?? null, code: t.code, name: t.name, impuesto: t.impuesto, tipoFactor: t.tipoFactor, tasaOCuota: t.tasaOCuota, objetoImp: t.objetoImp, traslado: t.traslado, retencion: t.retencion, validFrom: t.validFrom, validTo: t.validTo ?? null, active: t.active }))
}
export async function createCategory(input: Omit<M.Category, 'id'>): Promise<M.Category> {
  if (PREVIEW_STUB) { const c = { ...input, id: id('cat') }; db.categories.push(c); return mock(c) }
  // F8.x / ADR-0009: alta por el grafo (tenant del JWT, RLS).
  const gqlInput: { name: string; stationId?: string | null; sortOrder?: number; active?: boolean; publicVisible?: boolean } = { name: input.name }
  if (input.stationId !== undefined) gqlInput.stationId = input.stationId
  if (input.sortOrder !== undefined) gqlInput.sortOrder = input.sortOrder
  if (input.active !== undefined) gqlInput.active = input.active
  if (input.publicVisible !== undefined) gqlInput.publicVisible = input.publicVisible
  const res = await gqlClient.mutation(AdminCreateProductCategoryMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const row = res.data?.createProductCategory
  if (!row) throw new Error('No se pudo crear la categoría')
  return toUiCategory(row, input.sortOrder ? input.sortOrder - 1 : 0)
}
export async function updateCategory(c: M.Category): Promise<M.Category> {
  if (PREVIEW_STUB) { const i = db.categories.findIndex((x) => x.id === c.id); if (i >= 0) db.categories[i] = c; return mock(c) }
  // F8.x / ADR-0009: edición por el grafo (tenant del JWT, IDOR-guard).
  // stationId: null = limpiar ruteo, uuid = setear, undefined = sin cambio.
  const gqlInput: { name: string; stationId?: string | null; sortOrder?: number; active?: boolean; publicVisible?: boolean } = { name: c.name }
  if (c.stationId !== undefined) gqlInput.stationId = c.stationId
  if (c.sortOrder !== undefined) gqlInput.sortOrder = c.sortOrder
  if (c.active !== undefined) gqlInput.active = c.active
  if (c.publicVisible !== undefined) gqlInput.publicVisible = c.publicVisible
  const res = await gqlClient.mutation(AdminUpdateProductCategoryMutation, { id: c.id, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const row = res.data?.updateProductCategory
  if (!row) throw new Error('No se pudo actualizar la categoría')
  return toUiCategory(row, c.sortOrder - 1)
}
export async function deleteCategory(catId: string): Promise<void> {
  if (PREVIEW_STUB) { db.categories = db.categories.filter((x) => x.id !== catId); return mock(undefined) }
  // F8.x / ADR-0009: soft-delete por el grafo (tenant del JWT, IDOR-guard).
  const res = await gqlClient.mutation(AdminDeleteProductCategoryMutation, { id: catId }).toPromise()
  if (res.error) throw res.error
}

// ── products ───────────────────────────────────────────────────────────────
export async function listProducts(signal?: AbortSignal): Promise<M.Product[]> {
  if (PREVIEW_STUB) return mock(db.products)
  // type=PRODUCT mantiene los INGREDIENT fuera del catálogo. Los modificadores
  // también viven como type=PRODUCT pero tagged 'MODIFICADOR' → se excluyen aquí
  // para que la pestaña Productos solo muestre lo vendible. (El grafo ya pagina y
  // es tenant-scoped por el JWT — se acabó el truncado a 100 que sacaba UUIDs.)
  const all = await fetchAllProductsFromGraph('PRODUCT', signal)
  return all.filter((p) => !p.tags.includes('MODIFICADOR')).map(toUiProduct)
}
export async function getProduct(pId: string, signal?: AbortSignal): Promise<M.Product> {
  if (PREVIEW_STUB) { const p = db.products.find((x) => x.id === pId); if (!p) throw new Error('Producto no encontrado'); return mock(p) }
  // F6.2 R1 / ADR-0008: lectura singular por el grafo (erp-products `product(id)`,
  // tenant del JWT, IDOR-guard por RLS). Sustituye GET REST /v1/products/{id}.
  // network-only: el document-cache de urql no invalida la query tras
  // delete/create/update (deleteProduct devuelve Boolean, sin __typename de Product)
  // → reload() servía datos viejos. Forzamos red para que la vista refleje el cambio.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(AdminProductQuery, { id: pId }, ctx).toPromise()
  if (res.error) throw res.error
  const row = res.data?.product
  if (!row) throw new Error('Producto no encontrado')
  return toUiProduct({ ...row, description: row.description ?? null, categoryId: row.categoryId ?? null, imageUrl: row.imageUrl ?? null, unit: row.unit ?? null, type: row.type as ApiProduct['type'], tags: row.tags ?? [] })
}

/**
 * List INGREDIENT-typed rows (insumos) via the dedicated dhk8-erp-products
 * endpoint. Used by RecipeDetailPage to resolve `recipe.ingredients[*].productId`
 * to a name (these reference INGREDIENT rows, which `listProducts` excludes).
 * Also surfaces the `needs-attention:cost-0` tag the SAVIs onboarding sets on
 * insumos that came in with #DIV/0! in the Excel.
 */
export async function listIngredients(signal?: AbortSignal): Promise<M.Product[]> {
  if (PREVIEW_STUB) return mock(db.products.filter((p) => p.type === 'INGREDIENT'))
  const all = await fetchAllProductsFromGraph('INGREDIENT', signal)
  return all.map(toUiProduct)
}

/**
 * Like `listProducts` but returns ALL types (PRODUCT + INGREDIENT + COMBO +
 * SERVICE). Used by purchase-order details to resolve line `productId`s — a
 * PO line can reference an INGREDIENT (raw materials are the usual case) or
 * a PRODUCT (resale). MODIFICADOR rows are still filtered out — they aren't
 * physical things you buy.
 */
export async function listAllProducts(signal?: AbortSignal): Promise<M.Product[]> {
  if (PREVIEW_STUB) return mock(db.products)
  // TODOS los tipos (PRODUCT + INGREDIENT + COMBO + SERVICE). Lo usan PO/recetas/
  // inventario para resolver `productId` por id. MODIFICADOR sigue filtrado (no
  // son cosas físicas que compres).
  const all = await fetchAllProductsFromGraph(undefined, signal)
  return all.filter((p) => !p.tags.includes('MODIFICADOR')).map(toUiProduct)
}
export async function createProduct(input: Omit<M.Product, 'id'>): Promise<M.Product> {
  if (PREVIEW_STUB) { const p = { ...input, id: id('p') }; db.products.push(p); return mock(p) }
  // F8.x / ADR-0009: alta por el grafo. tenantId lo pone el JWT (no va en el input).
  // `type` es editable en creación (PRODUCT/SERVICE/COMBO/INGREDIENT); default PRODUCT.
  const gqlInput: { sku: string; name: string; type: 'PRODUCT' | 'SERVICE' | 'COMBO' | 'INGREDIENT'; priceCents: number; currency: string; categoryId?: string; description?: string; unit?: string; imageUrl?: string; tracksInventory?: boolean; consumesRecipe?: boolean; satProductKey?: string; satUnitKey?: string } = {
    sku: input.sku, name: input.name, type: input.type ?? 'PRODUCT', priceCents: input.priceCents, currency: 'MXN',
  }
  if (input.categoryId) gqlInput.categoryId = input.categoryId
  if (input.description) gqlInput.description = input.description
  if (input.unit) gqlInput.unit = input.unit
  if (input.imageUrl) gqlInput.imageUrl = input.imageUrl
  if (input.tracksInventory !== undefined) gqlInput.tracksInventory = input.tracksInventory
  if (input.consumesRecipe !== undefined) gqlInput.consumesRecipe = input.consumesRecipe
  if (input.satProductKey) gqlInput.satProductKey = input.satProductKey
  if (input.satUnitKey) gqlInput.satUnitKey = input.satUnitKey
  const res = await gqlClient.mutation(AdminCreateProductMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const row = res.data?.createProduct
  if (!row) throw new Error('No se pudo crear el producto')
  return toUiProduct({ ...row, description: row.description ?? null, categoryId: row.categoryId ?? null, imageUrl: row.imageUrl ?? null, unit: row.unit ?? null, type: row.type as ApiProduct['type'], tags: row.tags ?? [] })
}
/**
 * Crea un INSUMO (Product type=INGREDIENT) por el grafo — el resolver de
 * erp-products honra `input.type` (graphql.ts ~L261). `sku` se genera del nombre
 * + sufijo único (es @@unique por tenant). `priceCents` = costo por unidad del
 * insumo (alimenta el costeo de receta). Usado por "crear insumo al vuelo".
 */
export async function createIngredient(input: { name: string; unit?: string; costCents: number }): Promise<M.Product> {
  if (PREVIEW_STUB) {
    const p: M.Product = { id: id('ing'), name: input.name, sku: '', priceCents: input.costCents, categoryId: '', available: true, taxRate: '0.16', type: 'INGREDIENT', unit: input.unit ?? 'g' }
    db.products.push(p); return mock(p)
  }
  const base = (input.name || 'INSUMO').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8) || 'INSUMO'
  const sku = `${base}-${Date.now().toString(36).slice(-5).toUpperCase()}`
  const gqlInput: { sku: string; name: string; type: 'INGREDIENT'; priceCents: number; currency: string; unit?: string } = {
    sku, name: input.name, type: 'INGREDIENT', priceCents: Math.max(0, Math.round(input.costCents)), currency: 'MXN',
  }
  if (input.unit) gqlInput.unit = input.unit
  const res = await gqlClient.mutation(AdminCreateProductMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const row = res.data?.createProduct
  if (!row) throw new Error('No se pudo crear el insumo')
  return toUiProduct({ ...row, description: row.description ?? null, categoryId: row.categoryId ?? null, imageUrl: row.imageUrl ?? null, unit: row.unit ?? null, type: row.type as ApiProduct['type'], tags: row.tags ?? [] })
}
export async function updateProduct(p: M.Product): Promise<M.Product> {
  if (PREVIEW_STUB) { const i = db.products.findIndex((x) => x.id === p.id); if (i >= 0) db.products[i] = p; return mock(p) }
  // F8.x / ADR-0009: edición por el grafo (tenant del JWT, IDOR-guard).
  const res = await gqlClient.mutation(AdminUpdateProductMutation, {
    id: p.id,
    input: {
      name: p.name, priceCents: p.priceCents, currency: 'MXN',
      categoryId: p.categoryId || null, active: p.available,
      description: p.description ?? null, unit: p.unit ?? null, imageUrl: p.imageUrl ?? null,
      tracksInventory: p.tracksInventory ?? false, consumesRecipe: p.consumesRecipe ?? false,
      satProductKey: p.satProductKey ?? null, satUnitKey: p.satUnitKey ?? null,
    },
  }).toPromise()
  if (res.error) throw res.error
  const row = res.data?.updateProduct
  if (!row) throw new Error('No se pudo actualizar el producto')
  return toUiProduct({ ...row, description: row.description ?? null, categoryId: row.categoryId ?? null, imageUrl: row.imageUrl ?? null, unit: row.unit ?? null, type: row.type as ApiProduct['type'], tags: row.tags ?? [] })
}
export async function deleteProduct(pId: string): Promise<void> {
  if (PREVIEW_STUB) { db.products = db.products.filter((x) => x.id !== pId); return mock(undefined) }
  // F8.x / ADR-0009: soft-delete por el grafo (tenant del JWT, IDOR-guard).
  const res = await gqlClient.mutation(AdminDeleteProductMutation, { id: pId }).toPromise()
  if (res.error) throw res.error
}
export async function setAvailability(pId: string, available: boolean): Promise<void> {
  if (PREVIEW_STUB) { const p = db.products.find((x) => x.id === pId); if (p) p.available = available; return mock(undefined) }
  // F8.x / ADR-0009: disponibilidad = toggle `active` por el grafo (IDOR-guard).
  const res = await gqlClient.mutation(AdminSetProductAvailabilityMutation, { id: pId, available }).toPromise()
  if (res.error) throw res.error
}

// ── variants ───────────────────────────────────────────────────────────────
// Variants live inline on the Product (dhk8_erp.product_variants joined via
// the listProducts response that already includes `variants[]`). The standalone
// `dhk8-catalog-variants` service exposes per-product CRUD only (`/api/products/
// :id/variants/...`) — there is no global `/v1/variants` to list everything;
// we synthesize that view by flat-mapping the product list. POST/DELETE go to
// the product-scoped endpoints in `dhk8-erp-products`.
export async function listVariants(signal?: AbortSignal): Promise<M.Variant[]> {
  if (PREVIEW_STUB) return mock(db.variants)
  // F1.4 / ADR-0009: las variantes viven inline en el Product (erp-products las
  // trae con la lista, sin N+1). Las leemos por el grafo y hacemos flat-map.
  // Paginamos (>100 productos) — el subgraph capa limit a 100. Escrituras de
  // variantes siguen REST (endpoints product-scoped en dhk8-erp-products).
  // network-only: el document-cache de urql no invalida la query tras
  // delete/create/update (deleteProduct devuelve Boolean, sin __typename de Product)
  // → reload() servía datos viejos. Forzamos red para que la vista refleje el cambio.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const flat: M.Variant[] = []
  for (let page = 1; page < 100; page++) {
    const res = await gqlClient.query(AdminProductVariantsQuery, { page, limit: 100 }, ctx).toPromise()
    if (res.error) throw res.error
    const pageData = res.data?.products
    const items = pageData?.items ?? []
    for (const p of items) {
      for (const v of p.variants) {
        flat.push({ id: v.id, productId: p.id, name: v.name, priceDeltaCents: v.priceCents })
      }
    }
    const total = pageData?.total ?? items.length
    if (items.length < 100 || page * 100 >= total) break
  }
  return flat
}
export async function createVariant(input: Omit<M.Variant, 'id'>): Promise<M.Variant> {
  if (PREVIEW_STUB) { const v = { ...input, id: id('v') }; db.variants.push(v); return mock(v) }
  // F9.4 / ADR-0009: alta de variante por el grafo (erp-products, product-scoped,
  // IDOR-guard por tenant del JWT). El input NO lleva tenantId (lo pone el JWT).
  const res = await gqlClient.mutation(AdminAddProductVariantMutation, {
    productId: input.productId,
    input: { sku: input.name, name: input.name, priceCents: input.priceDeltaCents },
  }).toPromise()
  if (res.error) throw res.error
  const v = res.data?.addProductVariant
  if (!v) throw new Error('createVariant: empty response')
  return { id: v.id, productId: v.productId, name: v.name, priceDeltaCents: v.priceCents }
}
export async function deleteVariant(vId: string, productId?: string): Promise<void> {
  if (PREVIEW_STUB) { db.variants = db.variants.filter((x) => x.id !== vId); return mock(undefined) }
  // F9.4 / ADR-0009: soft-delete de variante por el grafo (product-scoped, IDOR-guard).
  // El productId sigue siendo requerido por la mutation (la variante se valida por su producto padre).
  if (!productId) throw new Error('deleteVariant: productId is required for the product-scoped mutation')
  const res = await gqlClient.mutation(AdminDeleteProductVariantMutation, { productId, variantId: vId }).toPromise()
  if (res.error) throw res.error
}

// ── A5: variantes multi-eje (catalog-variants) ──────────────────────────────
// Modelo: VariantAxis (eje, ej "Tamaño") → VariantOption por eje (label + delta)
// → VariantSku (combinación de optionIds con precio). Tenant del JWT (RLS).
export type VariantPriceMode = 'SUM_DELTAS' | 'ABSOLUTE'
export interface UiVariantOption { id: string; axisId: string; label: string; priceDeltaCents: number; sortOrder: number }
export interface UiVariantAxis { id: string; productId: string; name: string; sortOrder: number; options: UiVariantOption[] }
export interface UiVariantSku { id: string; productId: string; sku: string; optionIds: string[]; priceCents: number | null; priceMode: VariantPriceMode; active: boolean }

export async function listVariantAxes(productId: string, signal?: AbortSignal): Promise<UiVariantAxis[]> {
  if (PREVIEW_STUB) return mock([] as UiVariantAxis[])
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(AdminVariantAxesQuery, { productId }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.variantAxes ?? []).map((a) => ({
    id: a.id, productId: a.productId, name: a.name, sortOrder: a.sortOrder,
    options: a.options.map((o) => ({ id: o.id, axisId: o.axisId, label: o.label, priceDeltaCents: o.priceDeltaCents, sortOrder: o.sortOrder })),
  }))
}
export async function listVariantSkus(productId: string, signal?: AbortSignal): Promise<UiVariantSku[]> {
  if (PREVIEW_STUB) return mock([] as UiVariantSku[])
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(AdminVariantSkusQuery, { productId }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.variantSkus ?? []).map((s) => ({ id: s.id, productId: s.productId, sku: s.sku, optionIds: [...s.optionIds], priceCents: s.priceCents ?? null, priceMode: s.priceMode as VariantPriceMode, active: s.active }))
}
export async function upsertVariantAxis(input: { id?: string; productId: string; name: string; sortOrder?: number }): Promise<UiVariantAxis> {
  if (PREVIEW_STUB) return mock({ id: input.id ?? id('axis'), productId: input.productId, name: input.name, sortOrder: input.sortOrder ?? 0, options: [] })
  const gqlInput: { id?: string; productId: string; name: string; sortOrder?: number } = { productId: input.productId, name: input.name }
  if (input.id) gqlInput.id = input.id
  if (input.sortOrder !== undefined) gqlInput.sortOrder = input.sortOrder
  const res = await gqlClient.mutation(AdminUpsertVariantAxisMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const a = res.data?.upsertVariantAxis
  if (!a) throw new Error('No se pudo guardar el eje de variación')
  return { id: a.id, productId: a.productId, name: a.name, sortOrder: a.sortOrder, options: a.options.map((o) => ({ id: o.id, axisId: o.axisId, label: o.label, priceDeltaCents: o.priceDeltaCents, sortOrder: o.sortOrder })) }
}
export async function deleteVariantAxis(axisId: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const res = await gqlClient.mutation(AdminDeleteVariantAxisMutation, { id: axisId }).toPromise()
  if (res.error) throw res.error
}
export async function upsertVariantOption(input: { id?: string; axisId: string; label: string; priceDeltaCents?: number; sortOrder?: number }): Promise<UiVariantOption> {
  if (PREVIEW_STUB) return mock({ id: input.id ?? id('opt'), axisId: input.axisId, label: input.label, priceDeltaCents: input.priceDeltaCents ?? 0, sortOrder: input.sortOrder ?? 0 })
  const gqlInput: { id?: string; axisId: string; label: string; priceDeltaCents?: number; sortOrder?: number } = { axisId: input.axisId, label: input.label }
  if (input.id) gqlInput.id = input.id
  if (input.priceDeltaCents !== undefined) gqlInput.priceDeltaCents = input.priceDeltaCents
  if (input.sortOrder !== undefined) gqlInput.sortOrder = input.sortOrder
  const res = await gqlClient.mutation(AdminUpsertVariantOptionMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.upsertVariantOption
  if (!o) throw new Error('No se pudo guardar la opción de variación')
  return { id: o.id, axisId: o.axisId, label: o.label, priceDeltaCents: o.priceDeltaCents, sortOrder: o.sortOrder }
}
export async function deleteVariantOption(optionId: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const res = await gqlClient.mutation(AdminDeleteVariantOptionMutation, { id: optionId }).toPromise()
  if (res.error) throw res.error
}
export async function upsertVariantSku(input: { id?: string; productId: string; sku: string; optionIds?: string[]; priceCents?: number | null; priceMode?: VariantPriceMode; active?: boolean }): Promise<UiVariantSku> {
  if (PREVIEW_STUB) return mock({ id: input.id ?? id('sku'), productId: input.productId, sku: input.sku, optionIds: input.optionIds ?? [], priceCents: input.priceCents ?? null, priceMode: input.priceMode ?? 'SUM_DELTAS', active: input.active ?? true })
  const gqlInput: { id?: string; productId: string; sku: string; optionIds?: string[]; priceCents?: number; priceMode?: VariantPriceMode; active?: boolean } = { productId: input.productId, sku: input.sku }
  if (input.id) gqlInput.id = input.id
  if (input.optionIds !== undefined) gqlInput.optionIds = input.optionIds
  if (input.priceCents != null) gqlInput.priceCents = input.priceCents
  if (input.priceMode !== undefined) gqlInput.priceMode = input.priceMode
  if (input.active !== undefined) gqlInput.active = input.active
  const res = await gqlClient.mutation(AdminUpsertVariantSkuMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const s = res.data?.upsertVariantSku
  if (!s) throw new Error('No se pudo guardar el SKU de variante')
  return { id: s.id, productId: s.productId, sku: s.sku, optionIds: [...s.optionIds], priceCents: s.priceCents ?? null, priceMode: s.priceMode as VariantPriceMode, active: s.active }
}
export async function deleteVariantSku(skuId: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const res = await gqlClient.mutation(AdminDeleteVariantSkuMutation, { id: skuId }).toPromise()
  if (res.error) throw res.error
}

// ── modifiers ──────────────────────────────────────────────────────────────
// The backend zod enum for product `type` only allows PRODUCT | SERVICE | COMBO |
// INGREDIENT (no MODIFIER). Modifiers therefore live as type='PRODUCT' rows
// tagged with 'MODIFICADOR' so we can filter them out of the Productos tab
// while still surfacing them here. Tags VARIANTE/EXTRA OPCIONAL discriminate
// price model (free vs surcharge).
export async function listModifiers(signal?: AbortSignal): Promise<M.Modifier[]> {
  if (PREVIEW_STUB) return mock(db.modifiers)
  // F6.2 R1 / ADR-0008: lectura por el grafo (erp-products `products`, tenant del
  // JWT, RLS, paginado). Los modificadores viven como type=PRODUCT tagged
  // 'MODIFICADOR' → se filtran client-side. Sustituye GET REST /v1/products.
  const all = await fetchAllProductsFromGraph('PRODUCT', signal)
  return all
    .filter((p) => p.tags.includes('MODIFICADOR'))
    .map((r) => ({ id: r.id, name: r.name, priceCents: r.priceCents, groupName: 'Modificadores', required: false }))
}
export async function createModifier(input: Omit<M.Modifier, 'id'>): Promise<M.Modifier> {
  if (PREVIEW_STUB) { const m = { ...input, id: id('m') }; db.modifiers.push(m); return mock(m) }
  // F6.2 R3a / ADR-0008: alta por el grafo. Un modificador es un type=PRODUCT tagged
  // 'MODIFICADOR' (el enum de `type` no admite MODIFIER). tenantId lo pone el JWT.
  // Sustituye POST REST /v1/products. El input gql ahora soporta `tags` (aditivo).
  const res = await gqlClient.mutation(AdminCreateProductMutation, {
    input: { name: input.name, priceCents: input.priceCents, type: 'PRODUCT', tags: ['MODIFICADOR'], currency: 'MXN', sku: `MOD-${Date.now().toString(36)}` },
  }).toPromise()
  if (res.error) throw res.error
  const row = res.data?.createProduct
  if (!row) throw new Error('No se pudo crear el modificador')
  return { id: row.id, name: row.name, priceCents: row.priceCents, groupName: 'Modificadores', required: false }
}
export async function deleteModifier(mId: string): Promise<void> {
  if (PREVIEW_STUB) { db.modifiers = db.modifiers.filter((x) => x.id !== mId); return mock(undefined) }
  // F6.2 R1 / ADR-0008: soft-delete por el grafo (un modificador es type=PRODUCT,
  // IDOR-guard por RLS). Sustituye DELETE REST /v1/products/{id}.
  const res = await gqlClient.mutation(AdminDeleteProductMutation, { id: mId }).toPromise()
  if (res.error) throw res.error
}

// ── combos ─────────────────────────────────────────────────────────────────
// dhk8-catalog-combos exposes `/api/combos` (not `/v1/combos`). For v1 the
// SAVIs combos are stored as flat products in category "Combos" (the merchant
// did not provide composition in the Excel); we still surface that view here.
// When a proper Combo entity workflow exists, this client can switch to the
// catalog-combos endpoints.
export async function listCombos(signal?: AbortSignal): Promise<M.Combo[]> {
  if (PREVIEW_STUB) return mock(db.combos)
  // F6.2 R1 / ADR-0008: lectura por el grafo (erp-products `products` con
  // category { name }, tenant del JWT, RLS, paginado). Los combos v1 son
  // productos type=PRODUCT en la categoría "Combos" → se filtran client-side.
  // Sustituye GET REST /v1/products.
  // network-only: el document-cache de urql no invalida la query tras
  // delete/create/update (deleteProduct devuelve Boolean, sin __typename de Product)
  // → reload() servía datos viejos. Forzamos red para que la vista refleje el cambio.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const out: M.Combo[] = []
  for (let page = 1; page < 100; page++) {
    const res = await gqlClient.query(AdminComboProductsQuery, { page, limit: 100 }, ctx).toPromise()
    if (res.error) throw res.error
    const pageData = res.data?.products
    const items = pageData?.items ?? []
    for (const r of items) {
      if ((r.category?.name ?? '').toLowerCase() === 'combos') {
        out.push({ id: r.id, name: r.name, priceCents: r.priceCents, itemIds: [] as string[] })
      }
    }
    const total = pageData?.total ?? items.length
    if (items.length < 100 || page * 100 >= total) break
  }
  return out
}
export async function createCombo(input: Omit<M.Combo, 'id'>): Promise<M.Combo> {
  if (PREVIEW_STUB) { const c = { ...input, id: id('cmb') }; db.combos.push(c); return mock(c) }
  // Until composition (ComboItem[]) capture is wired in the UI, create as a flat
  // product in category Combos. The merchant edits name + price; sub-items are
  // a v1.1 feature.
  throw new Error('Creación de combos via wizard pendiente (fase 2). Por ahora se cargan como productos en categoría "Combos".')
}
export async function deleteCombo(cId: string): Promise<void> {
  if (PREVIEW_STUB) { db.combos = db.combos.filter((x) => x.id !== cId); return mock(undefined) }
  // F6.2 R1 / ADR-0008: soft-delete por el grafo (un combo v1 es type=PRODUCT,
  // IDOR-guard por RLS). Sustituye DELETE REST /v1/products/{id}.
  const res = await gqlClient.mutation(AdminDeleteProductMutation, { id: cId }).toPromise()
  if (res.error) throw res.error
}

// ── price lists ────────────────────────────────────────────────────────────
// dhk8-catalog-pricing requires tenantId query param and wraps the list in
// { items, total, page, limit }. Adapt the response shape to the UI's flat array
// and fall back to [] for empty tenants.
interface ApiPriceList { id: string; tenantId?: string; name: string; active: boolean; items?: { productId: string; priceCents: number }[] }
function toUiPriceList(p: ApiPriceList): M.PriceList {
  const overrides: Record<string, number> = {}
  for (const it of p.items ?? []) overrides[it.productId] = it.priceCents
  return { id: p.id, name: p.name, active: p.active, overrides }
}
export async function listPriceLists(signal?: AbortSignal): Promise<M.PriceList[]> {
  if (PREVIEW_STUB) return mock(db.priceLists)
  // F1.5 / ADR-0009: lectura por el grafo (catalog-pricing, tenant del JWT, RLS).
  // Escrituras de listas de precio siguen REST.
  // network-only: el document-cache de urql no invalida la query tras
  // delete/create/update (deleteProduct devuelve Boolean, sin __typename de Product)
  // → reload() servía datos viejos. Forzamos red para que la vista refleje el cambio.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(AdminPriceListsQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.priceLists.items ?? []).map((p) =>
    toUiPriceList({ id: p.id, name: p.name, active: p.active, items: p.items.map((i) => ({ productId: i.productId, priceCents: i.priceCents })) }),
  )
}
export async function createPriceList(input: Omit<M.PriceList, 'id'>): Promise<M.PriceList> {
  if (PREVIEW_STUB) { const pl = { ...input, id: id('pl') }; db.priceLists.push(pl); return mock(pl) }
  // F9.4 / ADR-0009: alta por el grafo (tenant del JWT, RLS). Sin tenantId en el input.
  const res = await gqlClient.mutation(AdminCreatePriceListMutation, { input: { name: input.name } }).toPromise()
  if (res.error) throw res.error
  const pl = res.data?.createPriceList
  if (!pl) throw new Error('createPriceList: empty response')
  return toUiPriceList({ id: pl.id, name: pl.name, active: pl.active, items: pl.items.map((i) => ({ productId: i.productId, priceCents: i.priceCents })) })
}
export async function setPriceListActive(plId: string, active: boolean): Promise<void> {
  if (PREVIEW_STUB) { const pl = db.priceLists.find((x) => x.id === plId); if (pl) pl.active = active; return mock(undefined) }
  const res = await gqlClient.mutation(AdminUpdatePriceListMutation, { id: plId, input: { active } }).toPromise()
  if (res.error) throw res.error
}
export async function deletePriceList(plId: string): Promise<void> {
  if (PREVIEW_STUB) { db.priceLists = db.priceLists.filter((x) => x.id !== plId); return mock(undefined) }
  const res = await gqlClient.mutation(AdminDeletePriceListMutation, { id: plId }).toPromise()
  if (res.error) throw res.error
}
// Renombrar una lista de precios (edición parcial; el resto de campos quedan igual).
export async function renamePriceList(plId: string, name: string): Promise<void> {
  if (PREVIEW_STUB) { const pl = db.priceLists.find((x) => x.id === plId); if (pl) pl.name = name; return mock(undefined) }
  const res = await gqlClient.mutation(AdminUpdatePriceListMutation, { id: plId, input: { name } }).toPromise()
  if (res.error) throw res.error
}
// Override de precio por producto dentro de una lista (upsert). priceCents = precio
// específico del producto en esa lista. El backend identifica el item por (lista,producto).
export async function setPriceListItem(plId: string, productId: string, priceCents: number): Promise<void> {
  if (PREVIEW_STUB) { const pl = db.priceLists.find((x) => x.id === plId); if (pl) pl.overrides[productId] = priceCents; return mock(undefined) }
  const res = await gqlClient.mutation(AdminAddPriceListItemMutation, { listId: plId, input: { productId, priceCents } }).toPromise()
  if (res.error) throw res.error
}
// Retira el override de un producto de la lista. El itemId del item es su productId
// (el tipo PriceListItem no expone un id propio; se identifica por producto).
export async function removePriceListItem(plId: string, productId: string): Promise<void> {
  if (PREVIEW_STUB) { const pl = db.priceLists.find((x) => x.id === plId); if (pl) delete pl.overrides[productId]; return mock(undefined) }
  const res = await gqlClient.mutation(AdminRemovePriceListItemMutation, { listId: plId, itemId: productId }).toPromise()
  if (res.error) throw res.error
}
